import { Button, Flex, Group, Tabs, Title } from '@mantine/core'
import { useForm } from '@mantine/form'
import {
  IconChevronLeft,
  IconChevronRight,
  IconFolder,
  IconVideo
} from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'
import { CameraStreamSelection } from '@/components/deployments/deployment-editor/CameraStreamSelection/CameraStreamSelection'
import { CameraStreamWithDeviceId } from '@/types/camera'
import { InputSourceType, MediaFolderConfig } from '@/types/deployment'
import { MediaFolderForm } from './MediaFolderForm'
import { InputSourceFormValues as FormValues } from './types'

export type { InputSourceFormValues as FormValues } from './types'

type InputSourceSelectionProps = {
  deviceIds: string[]
  initialInputSource: InputSourceType
  initialSelectedCameras: CameraStreamWithDeviceId[]
  initialMediaFolderConfig: MediaFolderConfig
  onContinue: (values: FormValues) => void
  onGoBack: () => void
}

export const InputSourceSelection = ({
  deviceIds,
  initialInputSource,
  initialSelectedCameras,
  initialMediaFolderConfig,
  onContinue,
  onGoBack
}: InputSourceSelectionProps) => {
  const intl = useIntl()

  const form = useForm<FormValues>({
    initialValues: {
      inputSource: initialInputSource,
      selectedCameras: initialSelectedCameras,
      mediaFolderConfig: initialMediaFolderConfig
    },

    validate: {
      selectedCameras: (value, values) => {
        if (values.inputSource !== InputSourceType.Cameras) {
          return null
        }

        if (value.length === 0 || value.some((c) => !c.connected)) {
          return intl.formatMessage({
            id: 'deployments.inputSource.camera.invalid'
          })
        }

        return null
      },
      mediaFolderConfig: {
        input_folder_path: (value, values) => {
          if (values.inputSource !== InputSourceType.MediaFolder) {
            return null
          }

          return value.trim() === ''
            ? intl.formatMessage({
                id: 'deployments.mediaFolder.inputFolder.required'
              })
            : null
        },
        output_folder_path: (value, values) => {
          if (values.inputSource !== InputSourceType.MediaFolder) {
            return null
          }

          if (value.trim() === '') {
            return intl.formatMessage({
              id: 'deployments.mediaFolder.outputFolder.required'
            })
          }

          if (
            value.trim() === values.mediaFolderConfig.input_folder_path.trim()
          ) {
            return intl.formatMessage({
              id: 'deployments.mediaFolder.outputFolder.samePath'
            })
          }

          return null
        }
      }
    }
  })

  const { inputSource } = form.getValues()

  const handleContinue = () => {
    const { hasErrors } = form.validate()
    if (hasErrors) return
    onContinue(form.getValues())
  }

  return (
    <>
      <Title order={3} mb="md">
        <FormattedMessage id="deployments.step4" />
      </Title>

      <Tabs
        variant="outline"
        value={inputSource}
        onChange={(value) => {
          form.setFieldValue('inputSource', value as InputSourceType)
          form.clearErrors()
        }}
      >
        <Tabs.List mb="md">
          <Tabs.Tab
            value={InputSourceType.Cameras}
            leftSection={<IconVideo size={16} />}
          >
            <FormattedMessage id="deployments.inputSource.camera" />
          </Tabs.Tab>

          <Tabs.Tab
            value={InputSourceType.MediaFolder}
            leftSection={<IconFolder size={16} />}
          >
            <FormattedMessage id="deployments.inputSource.mediaFolder" />
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value={InputSourceType.Cameras}>
          <CameraStreamSelection deviceIds={deviceIds} form={form} />
        </Tabs.Panel>

        <Tabs.Panel value={InputSourceType.MediaFolder}>
          <MediaFolderForm form={form} />
        </Tabs.Panel>
      </Tabs>

      <Flex justify="center" mt="md">
        <Group>
          <Button
            size="md"
            variant="outline"
            miw={200}
            leftSection={<IconChevronLeft size={16} stroke={3} />}
            onClick={onGoBack}
          >
            <FormattedMessage id="deployments.previousStep" />
          </Button>

          <Button
            size="md"
            miw={200}
            rightSection={<IconChevronRight size={16} stroke={3} />}
            onClick={handleContinue}
          >
            <FormattedMessage id="deployments.nextStep" />
          </Button>
        </Group>
      </Flex>
    </>
  )
}
